/**
 * Runs a skill's saves one at a time.
 *
 * A save asked for while another is running is not dropped: it runs once more when the
 * running one finishes and reads the editor as it stands then, so edits made meanwhile are
 * saved too. That holds after the editor unmounts as well, since the queue lives outside
 * React state. Any number of requests during one save run one more save, not one each.
 */
export interface SkillSaveQueue {
  /** Save the debounced draft without draining newer edits. */
  autosave: () => Promise<void>;
  getIsRunning: () => boolean;
  /** Save now, or after the running save. Settles when no save is left to run. */
  request: () => Promise<void>;
}

/**
 * `save` resolves to whether it succeeded (or had nothing to do). After a failure a queued
 * save is skipped: it would fail the same way, and the editor offers Retry instead.
 */
export function createSkillSaveQueue(
  save: () => Promise<boolean>,
  hasPendingChanges: () => boolean = () => false
): SkillSaveQueue {
  let running: Promise<void> | null = null;
  let queued = false;
  let flushPending = false;

  async function drain(): Promise<void> {
    try {
      let succeeded = true;
      do {
        queued = false;
        succeeded = await save();
      } while (succeeded && (queued || (flushPending && hasPendingChanges())));
    } finally {
      running = null;
      queued = false;
      flushPending = false;
    }
  }

  function request(flush: boolean) {
    flushPending ||= flush;
    if (running) {
      queued = true;
      return running;
    }
    running = Promise.resolve().then(drain);
    return running;
  }

  return {
    autosave: () => request(false),
    getIsRunning: () => running !== null,
    request: () => request(true),
  };
}
